import { formatMoneyString } from '@/utils/format-money-string';
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetOrderSummaries } from '@/api/documents/documents.hooks.ts';
import { resolveOrderStatus } from './order-status.util';

const PAGE_SIZE = 10;

export default function ExternalOrdersPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'docDate', sort: 'desc' },
  ]);

  // Debounce the free-text search and reset to the first page on a new term.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const sortBy = sortModel[0]?.field ?? 'docDate';
  const sortDir = sortModel[0]?.sort ?? 'desc';

  const { data, isPending, isFetching } = useGetOrderSummaries(
    page,
    search,
    sortBy,
    sortDir,
  );

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/bp/c/orders/${params.row.docEntry}`);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'docNum',
        headerName: 'Nr.',
        flex: 0.8,
        minWidth: 90,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'numAtCard',
        headerName: 'Ihre Bestellnr.',
        flex: 1.4,
        minWidth: 140,
        renderCell: (params) =>
          params.value ? (
            params.value
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: 'documentStatus',
        headerName: 'Status',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const status = resolveOrderStatus(params.value, params.row.cancelled);
          return (
            <Chip
              size="small"
              label={status.label}
              color={status.color}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          );
        },
      },
      {
        field: 'docDate',
        headerName: 'Datum',
        flex: 1.2,
        minWidth: 140,
        type: 'date',
        valueGetter: (value) => (value ? new Date(value) : null),
        renderCell: (params) =>
          params.value ? dayjs(params.value).format('dd, DD.MM.YYYY') : '—',
      },
      {
        field: 'docTotal',
        headerName: 'Gesamt',
        flex: 1,
        minWidth: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatMoneyString(params.value ?? 0)}
          </Typography>
        ),
      },
      {
        field: 'vatSum',
        headerName: 'Ust.',
        flex: 0.8,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatMoneyString(params.value ?? 0)}
          </Typography>
        ),
      },
    ],
    [],
  );

  return (
    <Stack spacing={2.5} sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <ReceiptLongIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Bestellungen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ihre Auftragshistorie
            </Typography>
          </Box>
        </Stack>

        <TextField
          size="small"
          placeholder="Nr., Bestellnr., Artikel suchen…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ width: { xs: '100%', sm: 280 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          height: {
            xs: 'calc(100dvh - 190px)',
            sm: 'calc(100dvh - 200px)',
          },
        }}
      >
        <DataGridPremium
          rows={data?.data ?? []}
          columns={columns}
          getRowId={(row) => row.docEntry}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          loading={isPending || isFetching}
          rowSelection={false}
          cellSelection={false}
          disableColumnMenu
          onRowClick={handleRowClick}
          rowCount={data?.total ?? 0}
          pagination
          paginationMode="server"
          pageSizeOptions={[PAGE_SIZE]}
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          onPaginationModelChange={(model) => setPage(model.page)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(
              model.length ? model : [{ field: 'docDate', sort: 'desc' }],
            );
            setPage(0);
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
            },
          }}
        />
      </Paper>
    </Stack>
  );
}
